import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { touchpointKeys, normalizeTouchpointListParams, type BulkMutationMeta } from "./query-keys";
import {
  TouchpointDTO,
  TouchpointDTOBatchJobExecutionStatus,
  TouchpointDTOContentType,
  TouchpointDTOPopulationType,
  TouchpointDTOClassesItem,
  TouchpointCreateRequest,
  TouchpointUpdateRequest,
  TouchpointPatchRequest,
  GetAllTouchpointsForCampaignParams,
  TouchpointVersionCloneRequest,
  TouchpointVersionCreateRequest,
  MondayBatchJobStatesDTO,
  CreateTouchpointsWithWriterRequest,
  RewriteTouchpointContentsRequest,
} from "generated/schemas";
import { getTouchpoint } from "generated/api/touchpoint";
import { getCampaign } from "generated/api/campaign";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointDetail, normalizeTouchpointListResponse, type TouchpointListResult } from "utilities/api-mappers";

import { TouchpointStatuses } from "types";
import { TouchpointType } from "types/touchpoint";

import { useApiContext, useRequiredApiContext } from "./helpers/api-context";
import { folderKeys } from "./query-keys";

export type TouchpointContentType = TouchpointDTOContentType | "";

/**
 * Aggregate view used across the touchpoint UI. Mirrors the union of fields
 * produced by Orval's `TouchpointDTO` and `CampaignTouchpointSummaryDTO`,
 * plus the few joined / derived fields the backend returns but does not put
 * in a single DTO (e.g. `latestVersion`, `validContentType`, `folderName`).
 * `mapTouchpointDetail` / `mapTouchpointSummary` in `utilities/api-mappers.ts`
 * are the source of truth.
 */
export interface TouchpointAttributes {
  folderId?: string;
  folderName?: string;
  publishedVersion?: number;
  campaignId: string;
  clientId: string;
  contentType: TouchpointContentType;
  description?: string;
  id: string;
  lastModifiedDate: string;
  name: string;
  status?: string;
  type: TouchpointType;
  version?: number;
  url?: string | null;
  pageDescription?: string | null;
  pageTitle?: string | null;
  subjectLine?: string | null;
  iterableTemplateId?: string | null;
  iterableEmailTypeId?: string | null;
  validContentType?: boolean;
  latestVersion?: {
    version: number;
    status: TouchpointStatuses;
    id: string;
    fiscalYear?: number;
    applyClientTheme?: boolean;
    aiGenerated?: boolean;
  };
  batchJobExecutionStatus?: TouchpointDTOBatchJobExecutionStatus;
  mondayBoardLink?: string;
  copyCode?: string;
  populationType?: TouchpointDTOPopulationType;
  classes?: TouchpointDTOClassesItem[];
  touchpointBatchId?: string;
  touchpointBatchName?: string;
  insightsActivator: boolean;
  templateCreativeId?: string;
}

const touchpointApi = getTouchpoint();
const campaignApi = getCampaign();
const touchpointVersionApi = getTouchpointVersion();

type TouchpointScope = {
  clientId: string;
  campaignId: string;
};

export function setTouchpointDetailCache(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: TouchpointScope,
  attributes: TouchpointAttributes,
  options?: { invalidateLists?: boolean },
) {
  queryClient.setQueryData<TouchpointAttributes>(
    touchpointKeys.detail(scope.clientId, scope.campaignId, attributes.id),
    (current) =>
      ({
        ...current,
        ...attributes,
      }),
  );

  if (options?.invalidateLists !== false) {
    queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
  }
}

export const useSetTouchpointDetailCache = () => {
  const queryClient = useQueryClient();

  return (
    scope: TouchpointScope,
    attributes: TouchpointAttributes,
    options?: { invalidateLists?: boolean },
  ) => {
    setTouchpointDetailCache(queryClient, scope, attributes, options);
  };
};

/**
 * Hook for fetching a single touchpoint by ID
 */
export const useTouchpoint = <TData = TouchpointAttributes>(
  id: string,
  options?: Omit<UseQueryOptions<TouchpointAttributes, Error, TData>, "queryKey" | "queryFn">,
  contextOverride?: {
    clientId?: string;
    campaignId?: string;
  },
) => {
  // Prefer overrides so this can run outside CampaignProvider (e.g. client-level
  // email-writer completion polling). Fall back to route/query context otherwise.
  const apiContext = useApiContext();
  const clientId = contextOverride?.clientId ?? apiContext.clientId;
  const campaignId = contextOverride?.campaignId ?? apiContext.campaignId;

  return useQuery({
    queryKey: touchpointKeys.detail(clientId ?? "", campaignId ?? "", id),
    queryFn: async () => {
      if (!clientId || !campaignId) {
        throw new Error("Context required for useTouchpoint queryFn");
      }
      const data = await touchpointApi.getTouchpoint({ clientId, campaignId, id });
      return mapTouchpointDetail(data, clientId, campaignId);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && Boolean(id) && (options?.enabled ?? true),
  });
};

/**
 * Hook for fetching campaign job states
 */
export const useCampaignJobStates = (
  options?: Omit<
    UseQueryOptions<MondayBatchJobStatesDTO, Error, MondayBatchJobStatesDTO>,
    "queryKey" | "queryFn"
  >,
) => {
  const { clientId, campaignId } = useRequiredApiContext();

  return useQuery({
    queryKey: touchpointKeys.jobStates(clientId, campaignId),
    queryFn: () => campaignApi.getBatchJobStates({ clientId, id: campaignId }),
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && (options?.enabled ?? true),
  });
};

export type UseTouchpointsParams = GetAllTouchpointsForCampaignParams & {
  clientId?: string;
  campaignId?: string;
};

/**
 * Hook for fetching a list of touchpoints (e.g., for a campaign).
 * Pass params object (use {} for default list).
 */
export const useTouchpoints = <TData = TouchpointListResult>(
  params: UseTouchpointsParams,
  options?: Omit<
    UseQueryOptions<TouchpointListResult, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const apiContext = useApiContext();
  // IMPORTANT: UseTouchpointsParams extends the API params with clientId/campaignId.
  // We must not pass those ids as query string params.
  const { clientId: clientIdOverride, campaignId: campaignIdOverride, ...listParams } = params;
  const clientId = clientIdOverride ?? apiContext.clientId;
  const campaignId = campaignIdOverride ?? apiContext.campaignId;

  const normalizedParams = normalizeTouchpointListParams(listParams);

  return useQuery({
    queryKey: touchpointKeys.list(clientId ?? "", campaignId ?? "", normalizedParams),
    queryFn: async () => {
      if (!clientId || !campaignId) {
        throw new Error("Context required for useTouchpoints queryFn");
      }
      const data = await touchpointApi.getAllTouchpointsForCampaign({ clientId, campaignId }, normalizedParams);
      return normalizeTouchpointListResponse(data, clientId, campaignId);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && (options?.enabled ?? true),
  });
};

/**
 * Mutation for creating a touchpoint (optionally with an initial version)
 */
export const useCreateTouchpoint = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: async ({
      data,
      versionData,
    }: {
      data: TouchpointCreateRequest;
      versionData?: TouchpointVersionCreateRequest;
    }) => {
      const touchpoint = await touchpointApi.createTouchpoint({ clientId, campaignId }, data);

      if (versionData && touchpoint.id) {
        const version = await touchpointVersionApi.createTouchpointVersion(
          { clientId, campaignId, touchpointId: touchpoint.id },
          versionData,
        );
        return { touchpoint, version };
      }

      return { touchpoint };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    },
  });
};

/**
 * Mutation for creating touchpoints in a campaign from writer creatives (strategy creative items).
 */
export const useCreateWriterTouchpoints = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: (data: CreateTouchpointsWithWriterRequest) =>
      touchpointApi.createWriterTouchpoints({ clientId, campaignId }, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    },
  });
};

export type RewriteTouchpointsWithWriterVariables = RewriteTouchpointContentsRequest & {
  clientId: string;
  campaignId: string;
};

/**
 * Mutation for rewriting email creatives in a campaign via background jobs. Rewrites every
 * eligible email creative when no touchpoint ids are supplied.
 */
export const useRewriteTouchpointsWithWriter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, campaignId, ...data }: RewriteTouchpointsWithWriterVariables) =>
      touchpointApi.rewriteEmailsAsync({ clientId, campaignId }, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    },
  });
};

/**
 * Mutation for updating a touchpoint
 */
export const useUpdateTouchpoint = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TouchpointUpdateRequest }) =>
      touchpointApi.updateTouchpoint({ clientId, campaignId, id }, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

export type PartiallyUpdateTouchpointVariables = {
  id: string;
  data: TouchpointPatchRequest;
  meta?: BulkMutationMeta;
};

/**
 * Mutation for partial update of a touchpoint.
 * Bulk: mutateAsync({ id, data, meta: { skipInvalidation: true } }) and invalidate once after the batch.
 */
export const usePartiallyUpdateTouchpoint = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: ({ id, data }: PartiallyUpdateTouchpointVariables) =>
      touchpointApi.partiallyUpdateTouchpoint({ clientId, campaignId, id }, data),
    onSuccess: (_, variables) => {
      if (variables.meta?.skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

export type DeleteTouchpointVariables = string | { id: string; meta?: BulkMutationMeta };

/**
 * Mutation for deleting (archiving) a touchpoint.
 * Single: mutate(id). Bulk: mutate({ id, meta: { skipInvalidation: true } }) and invalidate once after the batch.
 *
 * Accepts optional `contextOverride` so it can be used outside a CampaignProvider
 * (e.g. on the program-templates page).
 */
export const useDeleteTouchpoint = (contextOverride?: {
  clientId?: string;
  campaignId?: string;
}) => {
  const queryClient = useQueryClient();
  const apiContext = useApiContext();
  const clientId = contextOverride?.clientId ?? apiContext.clientId;
  const campaignId = contextOverride?.campaignId ?? apiContext.campaignId;

  return useMutation({
    mutationFn: (payload: DeleteTouchpointVariables) => {
      if (!clientId || !campaignId) {
        throw new Error(
          "useDeleteTouchpoint: clientId and campaignId are required. " +
            "Provide them via contextOverride or ensure a CampaignProvider is mounted.",
        );
      }
      const id = typeof payload === "string" ? payload : payload.id;
      return touchpointApi.deleteTouchpoint({ clientId, campaignId, id });
    },
    onSuccess: (_, variables) => {
      if (typeof variables === "object" && variables?.meta?.skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
};

/**
 * Mutation for cloning a touchpoint version
 */
export const useCloneTouchpoint = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: ({
      touchpointId,
      versionId,
      data,
    }: {
      touchpointId: string;
      versionId: string;
      data: TouchpointVersionCloneRequest;
    }) =>
      touchpointVersionApi.cloneTouchpoint(
        { clientId, campaignId, touchpointId, id: versionId },
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
};

/**
 * Mutation for retrying failed Monday sync.
 * Updates the React Query cache with the response to reflect new batchJobExecutionStatus.
 */
export const useRetrySyncTouchpoint = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: (touchpointId: string) =>
      touchpointApi.retrySyncTouchpoint({ clientId, campaignId, id: touchpointId }),
    onSuccess: (data) => {
      const attributes = mapTouchpointDetail(data, clientId, campaignId);
      setTouchpointDetailCache(queryClient, { clientId, campaignId }, attributes);
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

